import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Progress} from 'src/design/atoms/Progress';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {IconMapPin, IconPlus, IconQrcode} from '@tabler/icons-react';
import React, {useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import type {
  DeviceResponse,
  InspectionResponse,
  InspectionScopeDeviceResponse,
} from 'src/api/ProactApiClient';
import {DeviceScanModal} from 'src/app/inspections/DeviceScanModal';
import {
  ALL_LOCATIONS_NODE_ID,
  buildDeviceGroupTree,
  filterScopeDevices,
  findDeviceGroupNode,
} from 'src/app/inspections/deviceRollup';
import {LocationTree} from 'src/app/inspections/LocationTree';
import {Button} from 'src/design/atoms/Button';
import {Textbox} from 'src/design/atoms/Textbox';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {BrowserLayout} from 'src/design/organisms/BrowserLayout';
import {CustomDeviceModal} from 'src/pages/Modules/Inspections/shared/CustomDeviceModal';
import {useGetInspectionScopeDevicesQuery} from 'src/redux/queries/inspectionRecord';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useDeviceListUrlState} from 'src/app/inspections/useDeviceListUrlState';
import {
  DEVICE_ENTRY_GUIDED_PARAM,
  DEVICE_ENTRY_GUIDED_VALUE,
} from 'src/pages/Modules/Inspections/shared/deviceEntryParams';
import {InspectorDeviceTable} from './InspectorDeviceTable';
import styles from './InspectionMyExecuteScreen.module.css';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface InspectionMyExecuteScreenProps {
  inspection: InspectionResponse;
  /** Fired when the footer's Complete button is clicked — the parent page owns the
   *  `completing` screen switch to `CompletionConfirmPanel`. */
  onCompleteRequested: () => void;
}

/**
 * The inspector's tablet-oriented execution surface for `/my/:id`, mounted once
 * `inspection.state === InProgress` for the assignee — not a restyle of the admin monitor.
 */
export const InspectionMyExecuteScreen: React.FC<
  InspectionMyExecuteScreenProps
> = ({inspection, onCompleteRequested}) => {
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const {
    search,
    setSearch,
    nodeId,
    setNodeId,
    showOnlyRemaining,
    setShowOnlyRemaining,
  } = useDeviceListUrlState();

  const [scanModalOpened, setScanModalOpened] = useState(false);
  const [customDeviceModalOpened, setCustomDeviceModalOpened] = useState(false);

  const {data: devices} = useGetInspectionScopeDevicesQuery(inspection.id);
  const allDevices = useMemo(() => devices ?? [], [devices]);

  const tree = useMemo(() => buildDeviceGroupTree(allDevices), [allDevices]);
  const filteredDevices = useMemo(
    () => filterScopeDevices(allDevices, {nodeId, search, showOnlyRemaining}),
    [allDevices, nodeId, search, showOnlyRemaining]
  );

  const selectedNode =
    nodeId === ALL_LOCATIONS_NODE_ID
      ? undefined
      : findDeviceGroupNode(tree, nodeId);
  const selectedNodeLabel = selectedNode?.label ?? 'All locations';
  const selectedNodeCount = selectedNode?.counts.total ?? allDevices.length;

  const navigateToDeviceEntry = (device: InspectionScopeDeviceResponse) => {
    const params = new URLSearchParams(searchParams);
    params.set(DEVICE_ENTRY_GUIDED_PARAM, DEVICE_ENTRY_GUIDED_VALUE);
    navigate(
      `${PathUtility.InspectionsRecord(
        inspection.id,
        device.deviceId
      )}?${params.toString()}`
    );
  };

  const handleScanSearchFallback = (text: string) => {
    setSearch(text);
    setNodeId(ALL_LOCATIONS_NODE_ID);
  };

  const handleCustomDeviceCreated = (device: DeviceResponse) => {
    setCustomDeviceModalOpened(false);
    navigateToDeviceEntry({
      deviceId: device.id,
      name: device.name,
      serialNumber: device.serialNumber,
      building: device.building,
      floor: device.floor,
      room: device.room,
      equipmentTypeId: device.equipmentTypeId ?? 0,
      equipmentTypeName: undefined,
      deviceIsDeleted: false,
      isVerified: device.isVerified,
      inspectionDeviceId: undefined,
      complianceState: undefined,
    });
  };

  const enteredCount = inspection.scopedDeviceCount - inspection.remainingCount;
  const progressPct =
    inspection.scopedDeviceCount > 0
      ? Math.min(
          100,
          Math.max(0, (enteredCount / inspection.scopedDeviceCount) * 100)
        )
      : 0;

  if (allDevices.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <NoItemsPanel noItemsText="No devices in scope for this inspection." />
      </Box>
    );
  }

  return (
    <Box className={styles.screen}>
      <Group
        align="center"
        wrap="nowrap"
        gap="md"
        py="md"
        px="lg"
        className={styles.header}>
        <Stack gap="sm" className={styles.headerTitleGroup}>
          <Group justify="space-between" align="baseline" wrap="nowrap">
            <Text fw={700} size="lg" truncate className={styles.headerTitle}>
              {inspection.siteName}
            </Text>
            <Text size="sm" c="dimmed" className={styles.headerCount}>
              {enteredCount.toLocaleString()} of{' '}
              {inspection.scopedDeviceCount.toLocaleString()} inspected
            </Text>
          </Group>
          <Progress
            value={progressPct}
            size="sm"
            color={
              enteredCount >= inspection.scopedDeviceCount
                ? 'green.8'
                : 'green.6'
            }
            aria-label={`${enteredCount} of ${inspection.scopedDeviceCount} devices inspected`}
          />
        </Stack>
        <Button
          color="dark"
          size="sm"
          leftSection={<IconQrcode size={16} />}
          onClick={() => setScanModalOpened(true)}
          className={styles.scanButton}>
          Scan
        </Button>
      </Group>

      <BrowserLayout
        pane={
          <LocationTree
            tree={tree}
            totalCount={allDevices.length}
            selectedNodeId={nodeId}
            onSelectNode={setNodeId}
            legend="card"
          />
        }
        paneTitle="Locations"
        paneWidth={260}
        chipIcon={<IconMapPin size={14} />}
        selectionLabel={`${selectedNodeLabel} · ${selectedNodeCount}`}
        selectionKey={nodeId}
        classNames={{
          root: styles.body,
          pane: styles.treeColumn,
          content: styles.tableColumn,
        }}>
        <Group
          justify="space-between"
          wrap="wrap"
          gap="sm"
          pt="md"
          pb="md"
          className={styles.toolbar}>
          <Box className={styles.searchBox}>
            <Textbox
              ariaLabel="Search devices"
              placeholder="Search by name or serial…"
              value={search}
              onChangeCallback={event => setSearch(event.currentTarget.value)}
            />
          </Box>
          <Group gap="md" wrap="nowrap">
            <Text size="xs" c="dimmed">
              {filteredDevices.length} shown
            </Text>
            <Switch
              size="sm"
              label="Show only remaining"
              checked={showOnlyRemaining}
              onChange={event =>
                setShowOnlyRemaining(event.currentTarget.checked)
              }
            />
          </Group>
        </Group>

        <Box className={styles.tableWrapper}>
          <InspectorDeviceTable
            devices={filteredDevices}
            onEnterDevice={navigateToDeviceEntry}
          />
        </Box>
      </BrowserLayout>

      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        px="lg"
        py="sm"
        className={styles.footer}>
        <Button
          variant="default"
          size="md"
          leftSection={<IconPlus size={16} />}
          onClick={() => setCustomDeviceModalOpened(true)}>
          Add device
        </Button>

        <Group gap="sm" wrap="nowrap">
          {inspection.remainingCount > 0 && (
            <Text size="sm" c="dimmed" visibleFrom="sm">
              {inspection.remainingCount} remaining
            </Text>
          )}
          <Button
            size="md"
            color="yellow"
            disabled={inspection.remainingCount > 0}
            onClick={onCompleteRequested}>
            Complete inspection
          </Button>
        </Group>
      </Group>

      <DeviceScanModal
        opened={scanModalOpened}
        devices={allDevices}
        onClose={() => setScanModalOpened(false)}
        onOpenDevice={navigateToDeviceEntry}
        onSearchFallback={handleScanSearchFallback}
      />

      <CustomDeviceModal
        opened={customDeviceModalOpened}
        inspectionId={inspection.id}
        siteId={inspection.siteId}
        onClose={() => setCustomDeviceModalOpened(false)}
        onCreated={handleCustomDeviceCreated}
      />
    </Box>
  );
};
